// AI Turnover Scheduler — Analytics shim (Sprint 5).
//
// Single entry point for every scheduler-related analytics event so that
// `BulkOrdersPanel`, `ExternalBookingsTracker` and the upcoming Control Tower
// emit the same payload shape. We pipe events through:
//
//   1. GTM dataLayer (consent-gated, see `@/lib/analytics`).
//   2. The server-side log table `public.scheduler_decisions_log` via the
//      `record_scheduler_decision` RPC (best-effort; failures never block UI).
//
// The RPC call is fire-and-forget. We swallow errors on purpose: the engine
// must remain usable even if the log table is missing on a fresh checkout.

import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { pushGtmEvent } from "@/lib/analytics";
import {
  RULES_VERSION,
  type RiskLabel,
  type RiskReasonCode,
  type ServerSchedulerSummary,
} from "./types";
import type { SchedulerSource } from "./server";

export type SchedulerSurface =
  | "bulk_orders_panel"
  | "external_bookings_tracker"
  | "demand_control_tower"
  | "availability_editor";

export type SchedulerScope =
  | "batch"
  | "vendor_window"
  | "demand_batch"
  | "demand_portfolio";

export interface SchedulerSummaryEvent {
  surface: SchedulerSurface;
  scope: SchedulerScope;
  source: SchedulerSource;
  /** Always present for supply scopes; null for demand_portfolio when not bound. */
  companyId?: string | null;
  batchId?: string | null;
  profileId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  summary: ServerSchedulerSummary;
}

export interface SchedulerActionEvent {
  surface: SchedulerSurface;
  scope: SchedulerScope;
  /** Stable id for the suggested action button (UI-side). */
  actionId: string;
  actionLabel: string;
  source: SchedulerSource;
  companyId?: string | null;
  batchId?: string | null;
  riskLabel: RiskLabel;
  reasonCodes: RiskReasonCode[];
}

export interface SchedulerFallbackEvent {
  surface: SchedulerSurface;
  scope: SchedulerScope;
  reason: string;
  errorCode?: string;
  recoverable: boolean;
}

/** Best-effort write into `public.scheduler_decisions_log`. */
function recordDecisionAsync(payload: Record<string, unknown>): void {
  try {
    void (async () => {
      try {
        await supabaseUntyped.rpc("record_scheduler_decision", { p_payload: payload });
      } catch {
        // Intentionally swallowed: logging is best-effort.
      }
    })();
  } catch {
    // Defensive: never let analytics throw into the render path.
  }
}

export function trackSchedulerSummaryViewed(event: SchedulerSummaryEvent): void {
  const reasonCodes = event.summary.risk_reasons.map((r) => r.code);
  pushGtmEvent("scheduler_summary_viewed", {
    surface: event.surface,
    scope: event.scope,
    source: event.source,
    risk_label: event.summary.risk_label,
    confidence_score: event.summary.confidence_score,
    rules_version: event.summary.rules_version || RULES_VERSION,
    tight_count: event.summary.counts.tight,
    urgent_count: event.summary.counts.urgent,
    unscheduled_count: event.summary.counts.unscheduled,
    same_day_count: event.summary.counts.same_day,
    total_count: event.summary.counts.total,
    day_flags_count: event.summary.day_flags.length,
    reason_codes: reasonCodes,
    batch_id: event.batchId ?? undefined,
    company_id: event.companyId ?? undefined,
  });
  recordDecisionAsync({
    scope: event.scope,
    surface: event.surface,
    source: event.source,
    company_id: event.companyId ?? null,
    batch_id: event.batchId ?? null,
    profile_id: event.profileId ?? null,
    start_date: event.startDate ?? null,
    end_date: event.endDate ?? null,
    risk_label: event.summary.risk_label,
    risk_reasons: event.summary.risk_reasons,
    suggested_action: event.summary.suggested_action,
    confidence_score: event.summary.confidence_score,
    counts: event.summary.counts,
    day_flags_count: event.summary.day_flags.length,
    rules_version: event.summary.rules_version || RULES_VERSION,
    computed_at: event.summary.computed_at,
  });
}

export function trackSchedulerActionShown(event: SchedulerActionEvent): void {
  pushGtmEvent("scheduler_action_shown", {
    surface: event.surface,
    scope: event.scope,
    action_id: event.actionId,
    action_label: event.actionLabel,
    source: event.source,
    risk_label: event.riskLabel,
    reason_codes: event.reasonCodes,
    batch_id: event.batchId ?? undefined,
    company_id: event.companyId ?? undefined,
  });
}

export function trackSchedulerActionClicked(event: SchedulerActionEvent): void {
  pushGtmEvent("scheduler_action_clicked", {
    surface: event.surface,
    scope: event.scope,
    action_id: event.actionId,
    action_label: event.actionLabel,
    source: event.source,
    risk_label: event.riskLabel,
    reason_codes: event.reasonCodes,
    batch_id: event.batchId ?? undefined,
    company_id: event.companyId ?? undefined,
  });
}

export function trackSchedulerEngineFallback(event: SchedulerFallbackEvent): void {
  pushGtmEvent("scheduler_engine_fallback", {
    surface: event.surface,
    scope: event.scope,
    reason: event.reason,
    error_code: event.errorCode,
    recoverable: event.recoverable,
  });
}
